<template>
  <div class="ski-school-container">
    <!-- Header Section with Logo and Basic Info -->
    <div class="ski-school-header">
      <div class="logo-container">
        <img 
          :src="skiSchool.logo || defaultLogo" 
          alt="Ski School Logo" 
          class="ski-school-logo" 
        />
      </div>
      <div class="ski-school-info">
        <div class="name-container">
          <h2>{{ skiSchool.name }}</h2>
          <span class="country">{{ skiSchool.country }}</span>
        </div>
        <p class="description">{{ skiSchool.description }}</p>
        <div class="location-info">
          <p v-if="skiSchool.address" class="info-line">
            <i class="fas fa-map-marker-alt"></i> {{ skiSchool.address }}
          </p>
        </div>
        
        <!-- Action Buttons for Authenticated Users -->
        <div v-if="isAuthenticated && isUserCoach" class="school-buttons-container">
          <button class="school-action-button" @click="editSchool">
            <i class="fas fa-edit"></i>Edit School
          </button>
          <button class="school-action-button" @click="manageCoaches">
            <i class="fas fa-users"></i>Manage Coaches
          </button>
        </div>
      </div>
    </div>

    <!-- Introduction Video Section -->
    <div v-if="skiSchool.introduction_video" class="video-section">
      <h3>Introduction Video</h3>
      <video 
        :src="skiSchool.introduction_video" 
        controls 
        class="intro-video"
        preload="metadata"
      >
        Your browser does not support the video tag.
      </video>
    </div>

    <!-- Stats Section -->
    <div class="ski-school-stats">
      <div class="stat">
        <div class="stat-content">
          <span class="stat-number">{{ skiSchool.total_coaches || 0 }}</span>
          <p class="stat-label">coaches</p>
        </div>
      </div>
      <div class="stat" v-if="skiSchool.max_class_size">
        <div class="stat-content">
          <span class="stat-number">{{ skiSchool.max_class_size }}</span>
          <p class="stat-label">max class size</p>
        </div>
      </div>
      <div class="stat" v-if="skiSchool.season_duration_days">
        <div class="stat-content">
          <span class="stat-number">{{ skiSchool.season_duration_days }}</span>
          <p class="stat-label">season days</p>
        </div>
      </div>
      <div class="stat" v-if="skiSchool.price_per_set">
        <div class="stat-content">
          <span class="stat-number">{{ formatPrice(skiSchool.price_per_set, skiSchool.currency) }}</span>
          <p class="stat-label">per lesson</p>
        </div>
      </div>
    </div>

    <!-- Season Information -->
    <div v-if="skiSchool.season_start_date || skiSchool.season_end_date" class="season-section">
      <h3>Season Information</h3>
      <div class="season-info">
        <div v-if="skiSchool.season_start_date" class="season-date">
          <i class="fas fa-calendar-alt"></i>
          <span class="label">Season Start:</span>
          <span class="date">{{ formatDate(skiSchool.season_start_date) }}</span>
        </div>
        <div v-if="skiSchool.season_end_date" class="season-date">
          <i class="fas fa-calendar-alt"></i>
          <span class="label">Season End:</span>
          <span class="date">{{ formatDate(skiSchool.season_end_date) }}</span>
        </div>
      </div>
    </div>

    <!-- Coaches Section -->
    <div class="coaches-section">
      <h3>Coaches</h3>
      <div class="coaches-grid">
        <!-- Creator -->
        <div class="coach-card creator">
          <img 
            :src="skiSchool.creator_profile?.avatar || defaultAvatar" 
            :alt="skiSchool.creator_profile?.username" 
            class="coach-avatar"
          />
          <div class="coach-info">
            <span class="coach-name">
              {{ skiSchool.creator_profile?.first_name }} {{ skiSchool.creator_profile?.last_name }}
              <img
                v-if="skiSchool.creator_profile?.verified"
                src="@/assets/verified.png"
                alt="Verified"
                class="verified-badge"
                title="Verified Skier"
              />
              <router-link
                v-if="skiSchool.creator_profile?.ski_schools && skiSchool.creator_profile.ski_schools.length > 0"
                :to="`/ski-school/${skiSchool.creator_profile.ski_schools[0].slug}`"
                class="ski-school-link"
              >
                <img
                  :src="skiSchool.creator_profile.ski_schools[0].logo"
                  alt="Ski School"
                  class="ski-school-badge"
                  :title="`${skiSchool.creator_profile.ski_schools[0].name} - ${skiSchool.creator_profile.ski_schools[0].role}`"
                />
              </router-link>
              <span v-if="skiSchool.creator_profile?.verified" class="pro-badge"></span>
            </span>
            <span class="coach-username">@{{ skiSchool.creator_profile?.username }}</span>
            <span class="coach-role">Creator & Coach</span>
          </div>
        </div>
        
        <!-- Other Coaches -->
        <div 
          v-for="coach in skiSchool.coaches_detail" 
          :key="coach.id"
          class="coach-card"
        >
          <img 
            :src="coach.avatar || defaultAvatar" 
            :alt="coach.user?.username" 
            class="coach-avatar"
          />
          <div class="coach-info">
            <span class="coach-name">
              {{ coach.first_name }} {{ coach.last_name }}
              <img
                v-if="coach.verified"
                src="@/assets/verified.png"
                alt="Verified"
                class="verified-badge"
                title="Verified Skier"
              />
              <router-link
                v-if="coach.ski_schools && coach.ski_schools.length > 0"
                :to="`/ski-school/${coach.ski_schools[0].slug}`"
                class="ski-school-link"
              >
                <img
                  :src="coach.ski_schools[0].logo"
                  alt="Ski School"
                  class="ski-school-badge"
                  :title="`${coach.ski_schools[0].name} - ${coach.ski_schools[0].role}`"
                />
              </router-link>
              <span v-if="coach.verified" class="pro-badge"></span>
            </span>
            <span class="coach-username">@{{ coach.username }}</span>
            <span class="coach-role">Coach</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Contact Information -->
    <div class="contact-section">
      <h3>Contact Information</h3>
      <div class="contact-grid">
        <div v-if="skiSchool.contact_email" class="contact-item">
          <i class="fas fa-envelope"></i>
          <a :href="`mailto:${skiSchool.contact_email}`" class="contact-link">
            {{ skiSchool.contact_email }}
          </a>
        </div>
        <div v-if="skiSchool.contact_phone" class="contact-item">
          <i class="fas fa-phone"></i>
          <a :href="`tel:${skiSchool.contact_phone}`" class="contact-link">
            {{ skiSchool.contact_phone }}
          </a>
        </div>
        <div v-if="skiSchool.website_url" class="contact-item">
          <i class="fas fa-globe"></i>
          <a :href="skiSchool.website_url" target="_blank" class="contact-link">
            Website
          </a>
        </div>
      </div>
    </div>

    <!-- Social Media Links -->
    <div v-if="skiSchool.instagram_url || skiSchool.facebook_url" class="social-section">
      <h3>Follow Us</h3>
      <div class="social-links">
        <a 
          v-if="skiSchool.instagram_url" 
          :href="skiSchool.instagram_url" 
          target="_blank" 
          class="social-link instagram"
        >
          <i class="fab fa-instagram"></i>
          Instagram
        </a>
        <a 
          v-if="skiSchool.facebook_url" 
          :href="skiSchool.facebook_url" 
          target="_blank" 
          class="social-link facebook"
        >
          <i class="fab fa-facebook"></i>
          Facebook
        </a>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'SkiSchoolContainer',
  props: {
    skiSchool: {
      type: Object,
      required: true
    },
    isAuthenticated: {
      type: Boolean,
      default: false
    },
    userId: {
      type: Number,
      default: null
    }
  },
  data() {
    return {
      defaultLogo: "https://6buoys-bucket.s3.amazonaws.com/ski-school-logo.png",
      defaultAvatar: "https://6buoys-bucket.s3.amazonaws.com/avatar.png"
    }
  },
  computed: {
    isUserCoach() {
      // If not authenticated or no userId, return false
      if (!this.isAuthenticated || !this.userId) {
        return false;
      }

      // Check if user is the creator
      const isCreator = this.skiSchool.creator === this.userId;
      
      // Check if user is in the coaches list
      const isCoach = this.skiSchool.coaches_detail && 
        this.skiSchool.coaches_detail.some(coach => coach.user?.id === this.userId);
      
      // Also check the backend's is_user_coach field as a fallback
      const backendCheck = this.skiSchool.is_user_coach || false;
      
      const result = isCreator || isCoach || backendCheck;
      
      console.log(`SkiSchoolContainer for "${this.skiSchool.name}":`, {
        isAuthenticated: this.isAuthenticated,
        userId: this.userId,
        creator: this.skiSchool.creator,
        isCreator: isCreator,
        coaches: this.skiSchool.coaches_detail?.map(c => ({ id: c.user?.id, username: c.user?.username })),
        isCoach: isCoach,
        skiSchool_is_user_coach: this.skiSchool.is_user_coach,
        backendCheck: backendCheck,
        finalResult: result,
        shouldShowButtons: this.isAuthenticated && result
      });
      
      return result;
    }
  },
  methods: {
    formatDate(dateString) {
      if (!dateString) return "";
      const options = { month: "short", day: "numeric" };
      return new Date(dateString).toLocaleDateString(undefined, options);
    },
    formatPrice(price, currency) {
      const currencySymbols = {
        'USD': '$',
        'EUR': '€',
        'GBP': '£'
      };
      const symbol = currencySymbols[currency] || currency;
      return `${symbol}${parseFloat(price).toFixed(2)}`;
    },
    editSchool() {
      this.$emit('edit-school', this.skiSchool);
    },
    manageCoaches() {
      this.$emit('manage-coaches', this.skiSchool);
    }
  }
}
</script>

<style scoped>
.ski-school-container {
  max-width: 800px;
  margin: 0 auto;
  padding: 24px;
  background-color: #192734;
  border-radius: 16px;
  color: #d9d9d9;
  text-align: left;
  margin-bottom: 20px;
}

.ski-school-header {
  display: flex;
  align-items: flex-start;
  gap: 24px;
  margin-bottom: 24px;
}

.logo-container {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.ski-school-logo {
  width: 120px;
  height: 120px;
  min-width: 120px;
  min-height: 120px;
  border-radius: 12px;
  object-fit: cover;
  border: 2px solid #2a3f54;
}

.ski-school-info {
  flex: 1;
}

.name-container h2 {
  margin: 0 0 8px 0;
  color: #1da1f2;
  font-size: 1.8em;
}

.country {
  color: #8899a6;
  font-size: 1.1em;
  font-weight: 500;
}

.description {
  color: #d9d9d9;
  margin: 12px 0;
  line-height: 1.5;
}

.info-line {
  color: #8899a6;
  margin: 8px 0;
  display: flex;
  align-items: center;
  gap: 8px;
}

.info-line i {
  color: #1da1f2;
  width: 16px;
}

.video-section {
  margin: 24px 0;
}

.video-section h3 {
  color: #1da1f2;
  margin-bottom: 12px;
}

.intro-video {
  width: 100%;
  max-height: 400px;
  border-radius: 12px;
}

.ski-school-stats {
  display: flex;
  justify-content: space-around;
  margin: 24px 0;
  padding: 20px;
  background-color: #15202b;;
  border-radius: 12px;
}

.stat {
  text-align: center;
}

.stat-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
}

.stat-number {
  color: #1da1f2;
  font-size: 1.5em;
  font-weight: bold;
}

.stat-label {
  color: #8899a6;
  margin: 0;
  font-size: 0.9em;
}

.season-section,
.coaches-section,
.contact-section,
.social-section {
  margin: 24px 0;
}

.season-section h3,
.coaches-section h3,
.contact-section h3,
.social-section h3 {
  color: #1da1f2;
  margin-bottom: 16px;
  border-bottom: 1px solid #2a3f54;
  padding-bottom: 8px;
}

.season-info {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.season-date {
  display: flex;
  align-items: center;
  gap: 12px;
  color: #d9d9d9;
}

.season-date i {
  color: #1da1f2;
  width: 16px;
}

.season-date .label {
  font-weight: 500;
  min-width: 100px;
}

.coaches-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
}

.coach-card {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  background-color: #15202b;;
  border-radius: 8px;
}

.coach-card.creator {
  border: 1px solid #1da1f2;
}

.coach-avatar {
  width: 50px;
  height: 50px;
  border-radius: 50%;
  object-fit: cover;
}

.coach-info {
  display: flex;
  flex-direction: column;
}

.coach-name {
  color: #d9d9d9;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 4px;
}

.verified-badge {
  width: 16px;
  height: 16px;
}

.ski-school-badge {
  width: 16px;
  height: 16px;
  border-radius: 2px;
  object-fit: cover;
}

.ski-school-link {
  text-decoration: none;
  color: inherit;
}

.pro-badge {
  color: #1da1f2;
  font-weight: 500;
  font-size: 10px;
}

.coach-username {
  color: #8899a6;
  font-size: 0.9em;
}

.coach-role {
  color: #1da1f2;
  font-size: 0.8em;
  font-weight: 500;
}

.contact-grid {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.contact-item {
  display: flex;
  align-items: center;
  gap: 12px;
  color: #d9d9d9;
}

.contact-item i {
  color: #1da1f2;
  width: 20px;
}

.contact-link {
  color: #1da1f2;
  text-decoration: none;
}

.contact-link:hover {
  color: #1a91da;
  text-decoration: underline;
}

.social-links {
  display: flex;
  gap: 16px;
}

.social-link {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background-color: #0f1419;
  border-radius: 8px;
  text-decoration: none;
  color: #d9d9d9;
  transition: all 0.3s ease;
}

.social-link:hover {
  background-color: #2a3f54;
}

.social-link.instagram:hover {
  background-color: rgba(225, 48, 108, 0.2);
}

.social-link.facebook:hover {
  background-color: rgba(24, 119, 242, 0.2);
}

.school-buttons-container {
  margin-top: 16px;
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.school-action-button {
  display: inline-block;
  background-color: #1da1f2;
  color: #ffffff;
  border: none;
  padding: 8px 16px;
  border-radius: 16px;
  font-weight: bold;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.1);
}

.school-action-button i {
  margin-right: 6px;
  font-size: 11px;
}

.school-action-button:hover {
  background-color: #1a91da;
  transform: scale(1.02);
}

/* Mobile responsiveness */
@media (max-width: 768px) {
  .ski-school-header {
    flex-direction: column;
    align-items: center;
    text-align: center;
  }
  
  .ski-school-stats {
    flex-wrap: wrap;
    gap: 16px;
  }
  
  .coaches-grid {
    grid-template-columns: 1fr;
  }
  
  .social-links {
    flex-direction: column;
  }
}
</style> 